import React, { useCallback, useState } from "react";
import { useSearchParams } from "react-router-dom";
import AccountsList from "@/features/accounts/components/AccountsList";
import ClientSupportFilters, {
  EMPTY_CLIENT_SUPPORT_FILTERS,
  type ClientLookupScope,
  type ClientSupportFilterValue,
} from "@/features/accounts/components/ClientSupportFilters";
import { withAuthenticationRequired } from "@auth0/auth0-react";
import {
  Container,
  Card,
  Row as BootstrapRow,
  Col as BootstrapCol,
  Button,
  Modal,
  Form,
  Alert,
  Nav,
} from "react-bootstrap";
import { FilterOutlined, PlusOutlined } from "@ant-design/icons";
import PageTitle from "@/common/PageTitle/PageTitle";
import pageStyles from "./accounts.module.scss";
import {
  useCreateMasterAccountMutation,
  useListMasterAccountsQuery,
  useSendClientPasswordSetupEmailMutation,
} from "@/features/accounts/accountsApi";
import type { ClientLifecycleView } from "@/features/accounts/clientLifecycle";
import { formatTabCount } from "@/features/catalog-lifecycle/CatalogLifecycleTabs";
import { formatUsPhoneInput } from "@/utils/phoneFormat";

const emptyForm = {
  businessName: "",
  address_street: "",
  address_city: "",
  address_state: "",
  address_zipCode: "",
  address_country: "US",
  billingPhone: "",
  billingEmail: "",
  ownerFirstName: "",
  ownerLastName: "",
  ownerEmail: "",
  securityPin: "",
};

type ClientCreateSuccess = {
  masterAccountId: string;
  businessName: string | null;
  ownerEmail: string | null;
  ownerFirstName: string | null;
  passwordSetupUrl: string | null;
  copyFeedback: string | null;
  emailFeedback: string | null;
  emailError: string | null;
};

const Accounts: React.FC = () => {
  const [searchTerm] = useState("");
  const [view, setView] = useState<ClientLifecycleView>("active");
  const [filters, setFilters] = useState<ClientSupportFilterValue>(
    EMPTY_CLIENT_SUPPORT_FILTERS,
  );
  const [lookupScope, setLookupScope] = useState<ClientLookupScope>({
    masterAccountIds: null,
    result: { kind: "idle" },
    brandName: null,
    scopeKind: null,
  });
  const [resetCounter, setResetCounter] = useState(0);
  const [, setSearchParams] = useSearchParams();
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [formError, setFormError] = useState<string | null>(null);
  const [createSuccess, setCreateSuccess] =
    useState<ClientCreateSuccess | null>(null);

  const activeList = useListMasterAccountsQuery({
    kind: "client",
    view: "active",
  });
  const archivedList = useListMasterAccountsQuery({
    kind: "client",
    view: "archived",
  });
  const [createMasterAccount, { isLoading: creating }] =
    useCreateMasterAccountMutation();
  const [sendPasswordSetupEmail, { isLoading: emailing }] =
    useSendClientPasswordSetupEmailMutation();

  const handleFilterChange = useCallback(
    (patch: Partial<ClientSupportFilterValue>) => {
      setFilters((prev) => ({ ...prev, ...patch }));
    },
    [],
  );

  const handleReset = () => {
    setFilters(EMPTY_CLIENT_SUPPORT_FILTERS);
    setResetCounter((prev) => prev + 1);
  };

  // "Open Client Details" from a Store # lookup reuses the canonical
  // /accounts?client=<masterAccountId> deep-link handled by AccountsList.
  const openClientFromLookup = useCallback(
    (masterAccountId: string) => {
      setSearchParams({ client: masterAccountId }, { replace: true });
    },
    [setSearchParams],
  );

  const setField = (key: keyof typeof emptyForm, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleCreate = async () => {
    setFormError(null);
    setCreateSuccess(null);
    try {
      const result = await createMasterAccount({
        businessName: form.businessName.trim(),
        address_street: form.address_street.trim(),
        address_city: form.address_city.trim(),
        address_state: form.address_state.trim(),
        address_zipCode: form.address_zipCode.trim(),
        address_country: form.address_country.trim() || "US",
        billingPhone: form.billingPhone.trim() || undefined,
        companyNumber: form.billingPhone.trim() || undefined,
        billingEmail: form.billingEmail.trim() || form.ownerEmail.trim(),
        ownerFirstName: form.ownerFirstName.trim(),
        ownerLastName: form.ownerLastName.trim(),
        ownerEmail: form.ownerEmail.trim(),
        securityPin: form.securityPin.trim() || undefined,
      }).unwrap();
      setCreateSuccess({
        masterAccountId: result.masterAccount.masterAccountId,
        businessName: result.masterAccount.businessName || form.businessName.trim(),
        ownerEmail:
          result.ownerEmail || form.ownerEmail.trim().toLowerCase() || null,
        ownerFirstName:
          result.ownerFirstName || form.ownerFirstName.trim() || null,
        passwordSetupUrl: result.passwordSetupUrl || null,
        copyFeedback: null,
        emailFeedback: null,
        emailError: null,
      });
      setForm(emptyForm);
      setShowCreate(false);
    } catch (err: any) {
      setFormError(
        err?.data?.message || err?.message || "Failed to create client",
      );
    }
  };

  const handleCopySetupLink = async () => {
    const url = createSuccess?.passwordSetupUrl;
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCreateSuccess((prev) =>
        prev
          ? {
              ...prev,
              copyFeedback: "Password setup link copied",
              emailError: null,
            }
          : prev,
      );
    } catch {
      setCreateSuccess((prev) =>
        prev
          ? {
              ...prev,
              copyFeedback: null,
              emailError: "Could not copy link — select and copy manually",
            }
          : prev,
      );
    }
  };

  const handleEmailSetupLink = async () => {
    if (!createSuccess?.masterAccountId || !createSuccess.passwordSetupUrl) {
      return;
    }
    setCreateSuccess((prev) =>
      prev
        ? { ...prev, emailFeedback: null, emailError: null, copyFeedback: null }
        : prev,
    );
    try {
      const result = await sendPasswordSetupEmail({
        masterAccountId: createSuccess.masterAccountId,
        passwordSetupUrl: createSuccess.passwordSetupUrl,
      }).unwrap();
      setCreateSuccess((prev) =>
        prev
          ? {
              ...prev,
              emailFeedback: `Setup email submitted for delivery to ${result.to}`,
              emailError: null,
            }
          : prev,
      );
    } catch (err: any) {
      setCreateSuccess((prev) =>
        prev
          ? {
              ...prev,
              emailFeedback: null,
              emailError:
                err?.data?.message ||
                err?.message ||
                "Failed to submit password setup email",
            }
          : prev,
      );
    }
  };

  return (
    <Container fluid>
      <Card>
        <Card.Body>
          <Card.Title
            style={{ display: "flex", justifyContent: "space-between" }}
          >
            <PageTitle title="Clients" />
            <Button
              onClick={handleReset}
              style={{
                background: "white",
                color: "#333",
                border: "1px solid #e0e0e0",
                borderRadius: "6px",
                padding: "8px 16px",
                fontWeight: "500",
              }}
            >
              <FilterOutlined /> Reset All
            </Button>
          </Card.Title>

          {createSuccess && (
            <Alert
              variant="light"
              className={pageStyles.clientCreateSuccess}
              dismissible
              onClose={() => setCreateSuccess(null)}
              data-testid="client-create-success"
            >
              <div>Client created.</div>
              {createSuccess.passwordSetupUrl ? (
                <>
                  <div className="mt-1" style={{ wordBreak: "break-all" }}>
                    Password setup link: {createSuccess.passwordSetupUrl}
                  </div>
                  <div className="mt-3 d-flex flex-wrap gap-2 align-items-center">
                    <Button
                      size="sm"
                      variant="outline-dark"
                      className={pageStyles.clientCreateSuccessBtn}
                      onClick={handleCopySetupLink}
                      data-testid="client-create-copy-link"
                    >
                      Copy Link
                    </Button>
                    <Button
                      size="sm"
                      variant="outline-dark"
                      className={pageStyles.clientCreateSuccessBtn}
                      onClick={handleEmailSetupLink}
                      disabled={emailing}
                      data-testid="client-create-email-link"
                    >
                      {emailing ? "Sending…" : "Email Setup Link"}
                    </Button>
                    {createSuccess.copyFeedback && (
                      <span className={`small ${pageStyles.clientCreateSuccessFeedback}`}>
                        {createSuccess.copyFeedback}
                      </span>
                    )}
                  </div>
                  {createSuccess.emailFeedback && (
                    <div
                      className={`mt-2 small ${pageStyles.clientCreateSuccessFeedback}`}
                      data-testid="client-create-email-feedback"
                    >
                      {createSuccess.emailFeedback}
                    </div>
                  )}
                  {createSuccess.emailError && (
                    <div className="mt-2 text-danger small">
                      {createSuccess.emailError}
                    </div>
                  )}
                </>
              ) : (
                <div className="mt-1">
                  Share Auth0 password setup with the owner if needed.
                </div>
              )}
            </Alert>
          )}

          <Nav
            variant="tabs"
            activeKey={view}
            onSelect={(key) =>
              setView((key as ClientLifecycleView) || "active")
            }
            className="mb-3"
            data-testid="clients-lifecycle-tabs"
          >
            <Nav.Item>
              <Nav.Link eventKey="active">
                Active
                {formatTabCount(activeList.data?.masterAccounts?.length)}
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="archived">
                Archived
                {formatTabCount(archivedList.data?.masterAccounts?.length)}
              </Nav.Link>
            </Nav.Item>
          </Nav>

          <div className={pageStyles.form}>
            <BootstrapRow>
              <BootstrapCol>
                <ClientSupportFilters
                  value={filters}
                  onChange={handleFilterChange}
                  onScopeChange={setLookupScope}
                  onOpenClient={openClientFromLookup}
                  // Same cache entries the table renders — Brand-alone portfolio
                  // filtering reads row.brandIds from here (no extra request).
                  clients={
                    view === "archived"
                      ? archivedList.data?.masterAccounts
                      : activeList.data?.masterAccounts
                  }
                  resetKey={resetCounter}
                />
                {view === "active" ? (
                  <BootstrapRow className="mb-2">
                    <BootstrapCol
                      md={12}
                      className="d-flex justify-content-end"
                    >
                      <Button
                        style={{
                          background: "white",
                          color: "#e21e26",
                          border: "1px solid #e21e26",
                          borderRadius: "6px",
                          padding: "8px 16px",
                          fontWeight: "500",
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                          justifyContent: "center",
                          height: "50px",
                          marginTop: "8px",
                        }}
                        onClick={() => {
                          setFormError(null);
                          setShowCreate(true);
                        }}
                      >
                        <PlusOutlined style={{ fontSize: 16 }} />
                        Add New Client
                      </Button>
                    </BootstrapCol>
                  </BootstrapRow>
                ) : null}
              </BootstrapCol>
            </BootstrapRow>
          </div>

          <AccountsList
            searchTerm={searchTerm}
            filters={{
              status: filters.status,
              findClient: filters.findClient,
              masterAccountIds: lookupScope.masterAccountIds,
            }}
            view={view}
            onViewChange={setView}
          />
        </Card.Body>
      </Card>

      <Modal show={showCreate} onHide={() => setShowCreate(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Add New Client</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {formError && <Alert variant="danger">{formError}</Alert>}
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Company Name *</Form.Label>
              <Form.Control
                value={form.businessName}
                onChange={(e) => setField("businessName", e.target.value)}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Street *</Form.Label>
              <Form.Control
                value={form.address_street}
                onChange={(e) => setField("address_street", e.target.value)}
              />
            </Form.Group>
            <BootstrapRow>
              <BootstrapCol md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>City *</Form.Label>
                  <Form.Control
                    value={form.address_city}
                    onChange={(e) => setField("address_city", e.target.value)}
                  />
                </Form.Group>
              </BootstrapCol>
              <BootstrapCol md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>State *</Form.Label>
                  <Form.Control
                    value={form.address_state}
                    onChange={(e) => setField("address_state", e.target.value)}
                  />
                </Form.Group>
              </BootstrapCol>
              <BootstrapCol md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>ZIP *</Form.Label>
                  <Form.Control
                    value={form.address_zipCode}
                    onChange={(e) => setField("address_zipCode", e.target.value)}
                  />
                </Form.Group>
              </BootstrapCol>
            </BootstrapRow>
            <Form.Group className="mb-3">
              <Form.Label>Company Number (phone)</Form.Label>
              <Form.Control
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                placeholder="(888) 888-8888"
                value={form.billingPhone}
                onChange={(e) =>
                  setField("billingPhone", formatUsPhoneInput(e.target.value))
                }
              />
            </Form.Group>
            <BootstrapRow>
              <BootstrapCol md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Owner first name *</Form.Label>
                  <Form.Control
                    value={form.ownerFirstName}
                    onChange={(e) => setField("ownerFirstName", e.target.value)}
                  />
                </Form.Group>
              </BootstrapCol>
              <BootstrapCol md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Owner last name *</Form.Label>
                  <Form.Control
                    value={form.ownerLastName}
                    onChange={(e) => setField("ownerLastName", e.target.value)}
                  />
                </Form.Group>
              </BootstrapCol>
              <BootstrapCol md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Owner email *</Form.Label>
                  <Form.Control
                    type="email"
                    value={form.ownerEmail}
                    onChange={(e) => setField("ownerEmail", e.target.value)}
                  />
                </Form.Group>
              </BootstrapCol>
            </BootstrapRow>
            <Form.Group className="mb-3">
              <Form.Label>Billing email *</Form.Label>
              <Form.Control
                type="email"
                value={form.billingEmail}
                onChange={(e) => setField("billingEmail", e.target.value)}
                placeholder="Defaults to owner email if blank"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Security PIN (optional, 4 digits)</Form.Label>
              <Form.Control
                type="password"
                inputMode="numeric"
                autoComplete="new-password"
                maxLength={4}
                value={form.securityPin}
                onChange={(e) =>
                  setField(
                    "securityPin",
                    e.target.value.replace(/\D/g, "").slice(0, 4),
                  )
                }
              />
              <Form.Text muted>
                Optional. Exactly 4 digits. Used for phone verification — Super
                Admins can view it later from Client details.
              </Form.Text>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCreate(false)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            disabled={creating || !form.businessName.trim()}
            onClick={handleCreate}
          >
            {creating ? "Creating…" : "Create Client"}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default withAuthenticationRequired(Accounts);
